import { PriceIncrementWrapper } from '@src/containers/Trading/DeepDive/Aside/styles';
import { usePrimeFromOrderBook } from '@src/hooks/usePrimeFromOrderBook';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  Box,
  Button,
  EMPTY_ARRAY,
  Flex,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Tab,
  TabList,
  TabSize,
  Tabs,
  Text,
  isCross,
  isDelta1Multileg,
  useDynamicCallback,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
  useTabs,
  type TabProps,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { PriceIncrement } from '../Ladder/PriceIncrement';
import { OrderBook } from '../OrderBook/OrderBook';
import {
  IndividualOrderBook,
  IndividualOrderBookContent,
  IndividualOrderBookWrapper,
  OrderBookWrapper,
} from './components/IndividualOrderBook';
import { MultiLegOrderBookHeader, MultiLegOrderBookWrapper } from './styles';

const TABS: TabProps[] = [
  {
    label: 'Parent',
  },
  {
    label: 'Leg 1',
  },
  {
    label: 'Leg 2',
  },
];

interface MultilegOrderBookProps {
  symbol: string | undefined;
  allowCompact?: boolean;
  compactOrderBook?: boolean;
  showSymbolSelector?: boolean;
  onCompactClick?: (compactOrderBook: boolean | undefined) => void;
}

export const MultilegOrderBook = ({
  children,
  symbol,
  compactOrderBook,
  onCompactClick,
  allowCompact,
}: PropsWithChildren<MultilegOrderBookProps>) => {
  const tabs = useTabs({
    initialItems: TABS,
  });
  const [showCumQty, setShowCumQty] = useState(false);
  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const mixpanel = useMixpanel();

  const security = useSecurity(symbol);
  const resolvedSecurities = useMemo(
    () => security?.MultilegDetails?.Legs.map(leg => securitiesBySymbol.get(leg.Symbol)) ?? EMPTY_ARRAY,
    [security, securitiesBySymbol]
  );

  const [priceIncrement, setPriceIncrement] = useState<string | undefined>(security?.DefaultPriceIncrement);
  const [priceIncrementLeg1, setPriceIncrementLeg1] = useState<string | undefined>(
    resolvedSecurities[0]?.DefaultPriceIncrement
  );
  const [priceIncrementLeg2, setPriceIncrementLeg2] = useState<string | undefined>(
    resolvedSecurities[1]?.DefaultPriceIncrement
  );

  const handleIncrementChange = useDynamicCallback((index: number, newIncrement: string) => {
    const MinPriceIncrement = security?.MinPriceIncrement || '0.01';
    const priceIncrement = newIncrement < MinPriceIncrement ? MinPriceIncrement : newIncrement;
    setPriceIncrement(priceIncrement);
  });

  const handleLeg1IncrementChange = useDynamicCallback((index: number, newIncrement: string) => {
    const MinPriceIncrement = resolvedSecurities[0]?.MinPriceIncrement || '0.01';
    const priceIncrement = newIncrement < MinPriceIncrement ? MinPriceIncrement : newIncrement;
    setPriceIncrementLeg1(priceIncrement);
  });

  const handleLeg2IncrementChange = useDynamicCallback((index: number, newIncrement: string) => {
    const MinPriceIncrement = resolvedSecurities[1]?.MinPriceIncrement || '0.01';
    const priceIncrement = newIncrement < MinPriceIncrement ? MinPriceIncrement : newIncrement;
    setPriceIncrementLeg2(priceIncrement);
  });

  // this is a temporary limitation since we don't have the payout charts for delta1/cross if we compact we end up
  // having a big empty space. so in the meantime we just prevent them compacting
  const canCompact = useMemo(
    () => allowCompact ?? (!isDelta1Multileg(security) && !isCross(security)),
    [security, allowCompact]
  );
  const compactView = useMemo(() => (!canCompact ? false : compactOrderBook), [canCompact, compactOrderBook]);

  useEffect(() => {
    if (security) {
      if (priceIncrement == null) {
        setPriceIncrement(security.DefaultPriceIncrement);
      }
    }
    if (resolvedSecurities[0] && priceIncrementLeg1 == null) {
      setPriceIncrementLeg1(resolvedSecurities[0].DefaultPriceIncrement);
    }
    if (resolvedSecurities[1] && priceIncrementLeg2 == null) {
      setPriceIncrementLeg2(resolvedSecurities[1].DefaultPriceIncrement);
    }
  }, [security, resolvedSecurities, priceIncrement, priceIncrementLeg1, priceIncrementLeg2]);

  const handleCompactClick = useDynamicCallback(() => {
    if (compactView) {
      mixpanel.track(MixpanelEvent.ViewAllOrderBooks);
    }
    onCompactClick?.(compactOrderBook);
  });

  const leg1MarketAccounts = useMemo(
    () => security?.MultilegDetails?.Legs[0].Markets?.map(x => x.MarketAccount),
    [security]
  );
  const leg2MarketAccounts = useMemo(
    () => security?.MultilegDetails?.Legs[1].Markets?.map(x => x.MarketAccount),
    [security]
  );

  const handleCombinedRowClick = usePrimeFromOrderBook({ security });
  const handleLeg1RowClick = usePrimeFromOrderBook({
    security: resolvedSecurities[0],
    marketAccounts: leg1MarketAccounts,
  });
  const handleLeg2RowClick = usePrimeFromOrderBook({
    security: resolvedSecurities[1],
    marketAccounts: leg2MarketAccounts,
  });

  const fullView = (
    <Flex flex="1" gap="1px" background="backgroundBody" data-testid="multileg-orderbook-full">
      {security ? (
        <IndividualOrderBook
          security={security}
          marketAccounts={EMPTY_ARRAY}
          priceIncrement={priceIncrement}
          onIncrementChange={handleIncrementChange}
          hideLastTraded={true}
          showFirmLiquidity={showFirmLiquidity}
          showAllInPrices={showAllInPrices}
          showCumQty={showCumQty}
          onCumQtyClick={setShowCumQty}
          onRowClick={handleCombinedRowClick}
        />
      ) : (
        <Box background="backgroundContent" flex="1 1 33%" />
      )}
      {resolvedSecurities[0] ? (
        <IndividualOrderBook
          security={resolvedSecurities[0]}
          marketAccounts={leg1MarketAccounts ?? EMPTY_ARRAY}
          priceIncrement={priceIncrementLeg1}
          onIncrementChange={handleLeg1IncrementChange}
          showFirmLiquidity={showFirmLiquidity}
          showAllInPrices={showAllInPrices}
          showCumQty={showCumQty}
          onCumQtyClick={setShowCumQty}
          onRowClick={handleLeg1RowClick}
        />
      ) : (
        <Box background="backgroundContent" flex="1 1 33%" />
      )}
      {resolvedSecurities[1] ? (
        <IndividualOrderBook
          security={resolvedSecurities[1]}
          marketAccounts={leg2MarketAccounts ?? EMPTY_ARRAY}
          priceIncrement={priceIncrementLeg2}
          onIncrementChange={handleLeg2IncrementChange}
          showFirmLiquidity={showFirmLiquidity}
          showAllInPrices={showAllInPrices}
          showCumQty={showCumQty}
          onCumQtyClick={setShowCumQty}
          onRowClick={handleLeg2RowClick}
        />
      ) : (
        <Box background="backgroundContent" flex="1 1 33%" />
      )}
    </Flex>
  );

  const compactMinIncrement =
    tabs.selectedIndex === 0
      ? security?.MinPriceIncrement || '0.01'
      : tabs.selectedIndex === 1
        ? resolvedSecurities[0]?.MinPriceIncrement || '0.01'
        : resolvedSecurities[1]?.MinPriceIncrement || '0.01';

  const compactDefaultIncrement =
    tabs.selectedIndex === 0
      ? security?.DefaultPriceIncrement
      : tabs.selectedIndex === 1
        ? resolvedSecurities[0]?.DefaultPriceIncrement
        : resolvedSecurities[1]?.DefaultPriceIncrement;

  const compact = (
    <IndividualOrderBookWrapper data-testid="multileg-orderbook-compact">
      <IndividualOrderBookContent data-testid="multileg-order-book">
        <Tabs {...tabs} w="100%" position="relative" size={TabSize.Large} flex="0 0 auto">
          <TabList
            isBordered
            rightItems={
              <PriceIncrementWrapper>
                <PriceIncrement
                  min={compactMinIncrement}
                  value={priceIncrement || compactDefaultIncrement || '0.01'}
                  onChange={handleIncrementChange}
                />
              </PriceIncrementWrapper>
            }
          >
            {tabs.items.map((tab, index) => (
              <Tab key={index} {...tab} />
            ))}
          </TabList>
        </Tabs>
        <OrderBookWrapper>
          {tabs.selectedIndex === 0 && security && (
            <OrderBook
              security={security}
              priceIncrement={priceIncrement || security.DefaultPriceIncrement}
              hideLastTraded={true}
              selectedMarketAccounts={EMPTY_ARRAY}
              selectedMarkets={EMPTY_ARRAY}
              showFirmLiquidity={showFirmLiquidity}
              showAllInPrices={showAllInPrices}
              showCumQty={showCumQty}
              onCumQtyClick={setShowCumQty}
              onRowClick={handleCombinedRowClick}
              // We don't support click to trade for this order book
              clickToTradePreviewOrder={undefined}
              onMouseLeaveRow={noop}
            />
          )}
          {tabs.selectedIndex === 1 &&
            (resolvedSecurities[0] ? (
              <OrderBook
                security={resolvedSecurities[0]}
                priceIncrement={priceIncrement || resolvedSecurities[0].DefaultPriceIncrement}
                selectedMarketAccounts={leg1MarketAccounts ?? EMPTY_ARRAY}
                selectedMarkets={EMPTY_ARRAY}
                showFirmLiquidity={showFirmLiquidity}
                showAllInPrices={showAllInPrices}
                showCumQty={showCumQty}
                onCumQtyClick={setShowCumQty}
                onRowClick={handleLeg1RowClick}
                // We don't allow click to trade for these order books
                clickToTradePreviewOrder={undefined}
                onMouseLeaveRow={noop}
              />
            ) : (
              <Box />
            ))}
          {tabs.selectedIndex === 2 &&
            (resolvedSecurities[1] ? (
              <OrderBook
                security={resolvedSecurities[1]}
                priceIncrement={priceIncrement || resolvedSecurities[1].DefaultPriceIncrement}
                selectedMarketAccounts={leg2MarketAccounts ?? EMPTY_ARRAY}
                selectedMarkets={EMPTY_ARRAY}
                showFirmLiquidity={showFirmLiquidity}
                showAllInPrices={showAllInPrices}
                showCumQty={showCumQty}
                onCumQtyClick={setShowCumQty}
                onRowClick={handleLeg2RowClick}
                // We don't allow click to trade for these order books
                clickToTradePreviewOrder={undefined}
                onMouseLeaveRow={noop}
              />
            ) : (
              <Box />
            ))}
        </OrderBookWrapper>
      </IndividualOrderBookContent>
    </IndividualOrderBookWrapper>
  );

  return (
    <>
      <MultiLegOrderBookWrapper flex={compactView ? '1 1 460px' : '1 1 100%'} id="multileg-order-book">
        <MultiLegOrderBookHeader>
          {children ?? <Text color="colorTextAttention">Order Book</Text>}
          <Button
            size={FormControlSizes.Small}
            endIcon={IconName.ListCollapse}
            onClick={handleCompactClick}
            disabled={!canCompact}
          >
            {compactView ? 'Expand' : 'Compact'}
          </Button>
        </MultiLegOrderBookHeader>
        {compactView ? compact : fullView}
      </MultiLegOrderBookWrapper>
    </>
  );
};
